import { computed } from 'vue';
import {
  queryOptions,
  useMutation,
  useMutationState,
  useQueryClient,
} from '@tanstack/vue-query';
import {
  ORDER_FEATURE_KEYS,
  type OrderFeatureKey,
  type OrderFeatures,
} from '@cutr/constants/order-features';
import { getOrderFeatures, setOrderFeature } from '@/api/orderFeatures';
import { orderFeatureKeys, toggleFeatureMutationKey } from './keys';
import { FEATURES_STALE_TIME_MS } from './shared/staleTimes';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags } from './shared/types';

export const BACKFILL_POLL_INTERVAL_MS = 3000;

export function computeFeaturesRefetchInterval(features?: OrderFeatures) {
  const backfilling = Object.entries(features ?? {}).some(
    ([key, state]) =>
      ORDER_FEATURE_KEYS[key as OrderFeatureKey]?.hasBackfillHook &&
      state?.enabled &&
      !state.backfillCompletedAt
  );
  return backfilling ? BACKFILL_POLL_INTERVAL_MS : false;
}

export function orderFeaturesOptions(orderId: string) {
  return queryOptions({
    queryKey: orderFeatureKeys.list(orderId),
    queryFn: ({ signal }) => getOrderFeatures(orderId, signal),
    staleTime: FEATURES_STALE_TIME_MS,
  });
}

export function useOrderFeaturesQuery(orderId: MaybeId, flags?: QueryFlags) {
  const query = useScopedQuery(orderId, orderFeaturesOptions, {
    ...flags,
    refetchInterval: (query) =>
      computeFeaturesRefetchInterval(query.state.data),
  });

  return {
    features: computed<OrderFeatures>(() => query.data.value ?? {}),
    query,
  };
}

export type ToggleFeatureVariables = {
  orderId: string;
  featureKey: OrderFeatureKey;
  payload: { enabled: boolean; clearExisting?: boolean };
};

export function useOrderFeatureMutations() {
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationKey: toggleFeatureMutationKey,
    mutationFn: ({ orderId, featureKey, payload }: ToggleFeatureVariables) =>
      setOrderFeature(orderId, featureKey, payload),
    async onSuccess(state, { orderId, featureKey }) {
      const queryKey = orderFeatureKeys.list(orderId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<OrderFeatures>(queryKey, (current) => ({
        ...current,
        [featureKey]: state,
      }));
    },
  });

  return {
    toggleFeature: (variables: ToggleFeatureVariables) =>
      toggle.mutateAsync(variables),
  };
}

// One toggle observer serves every row of the features modal, so which rows
// are busy has to come from the mutations in flight rather than `isPending`.
// `toggleFeatureMutationKey` is shared by every order, so a pending toggle
// for order A is indistinguishable from one for order B without also
// keeping `orderId` here.
export function useTogglingFeatures() {
  return useMutationState({
    filters: { mutationKey: toggleFeatureMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as ToggleFeatureVariables | undefined,
  });
}
